import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { CheckCircle, XCircle, ArrowRight, Star, DollarSign } from 'lucide-react';

interface AltOption {
  name: string;
  slug: string;
  website: string;
  sites: string;
  startPrice: string;
  tagline: string;
  bestFor: string;
  pros: string[];
  cons: string[];
  score: number;
  free: boolean;
}

interface AlternativesData {
  subject: string;
  subjectSlug: string;
  subjectWebsite: string;
  subjectType: 'tool' | 'marketplace';
  title: string;
  intro: string;
  why: string;
  options: AltOption[];
  faqs: { q: string; a: string }[];
}

const ALTERNATIVES: Record<string, AlternativesData> = {
  fatgrid: {
    subject: 'FatGrid',
    subjectSlug: 'fatgrid',
    subjectWebsite: 'fatgrid.com',
    subjectType: 'tool',
    title: 'Best FatGrid Alternatives in 2026 (Free + Paid)',
    intro: 'FatGrid is one of the newer backlink price comparison tools — clean interface, SERP scanner, competitor scanning. But it only covers 15+ marketplaces vs. 50+ on the best alternatives, and the credit system for advanced features can add up. Here are the best FatGrid alternatives tested and ranked.',
    why: 'Most people look for FatGrid alternatives because the credit model for advanced features is not free, the marketplace coverage is limited to 15+ sellers (vs 50+ on competitors), and there are no quality scores or price alerts. The right alternative depends on what you need.',
    options: [
      {
        name: 'LinkDiry',
        slug: 'linkdiry',
        website: 'linkdiry.com',
        sites: '490K+ domains',
        startPrice: '$0 — free forever',
        tagline: 'The free FatGrid alternative with 50+ marketplaces',
        bestFor: 'Users who want FatGrid features without the credit system',
        pros: ['50+ marketplaces vs FatGrid\'s 15', 'Completely free — no credits, no limits', 'Quality scores, price alerts, competitor scanner', 'SERP scanner included', 'Chrome extension', 'AI relevance scoring'],
        cons: ['Newer brand than FatGrid'],
        score: 9.2,
        free: true,
      },
      {
        name: 'LinkBuilder.com',
        slug: 'linkbuilder',
        website: 'linkbuilder.com',
        sites: '339K+ domains',
        startPrice: '$8–$105/month',
        tagline: 'The original and largest backlink price comparison tool',
        bestFor: 'Agencies that need the most established tool with 50+ marketplaces',
        pros: ['50+ marketplaces covered', '4+ year track record', 'Country-level filtering', '7+ SEO metrics', 'Managed link building upsell'],
        cons: ['$8–$105/month', 'Dated UX', 'Weekly updates only', 'No quality scores'],
        score: 7.8,
        free: false,
      },
      {
        name: 'LinkDetective.pro',
        slug: 'linkdetective',
        website: 'linkdetective.pro',
        sites: '118K+ domains',
        startPrice: '$0 — free',
        tagline: 'Minimalist free bulk domain price checker',
        bestFor: 'Bulk checking — upload 100s of domains at once',
        pros: ['Free with no limits', 'Strong bulk upload feature', 'Simple, fast interface'],
        cons: ['Fewer marketplaces than FatGrid', 'No SERP scanner', 'No quality scores', 'Minimal features overall'],
        score: 6.5,
        free: true,
      },
    ],
    faqs: [
      { q: 'Is FatGrid free?', a: 'FatGrid offers free core domain search but charges credits for advanced features like SERP scanning (60 credits/scan), competitor backlink scanning (2 credits/domain), and link monitoring. The best free FatGrid alternative is LinkDiry, which offers all features for free with no credit system.' },
      { q: 'What are the best FatGrid alternatives?', a: 'The best FatGrid alternatives are: 1) LinkDiry — free, 50+ marketplaces, quality scores; 2) LinkBuilder.com — paid, 50+ marketplaces, longer track record; 3) LinkDetective.pro — free, simpler but with strong bulk checking. For most users, LinkDiry offers the best combination of features and zero cost.' },
      { q: 'How many marketplaces does FatGrid cover vs competitors?', a: 'FatGrid covers 15+ marketplaces. LinkDiry and LinkBuilder.com both cover 50+ marketplaces — over 3x more. This means FatGrid shows you prices from less than a third of available sellers, which can mean missing a significantly cheaper option.' },
      { q: 'Does LinkDiry have a SERP scanner like FatGrid?', a: 'Yes — LinkDiry\'s SERP scanner lets you scan any keyword SERP and see backlink prices for all ranking domains at once, identical to FatGrid\'s SERP scanner feature. LinkDiry\'s is completely free with no credit cost per scan.' },
    ],
  },
  linkbuilder: {
    subject: 'LinkBuilder.com',
    subjectSlug: 'linkbuilder',
    subjectWebsite: 'linkbuilder.com',
    subjectType: 'tool',
    title: 'Best LinkBuilder.com Alternatives in 2026 (Free + Paid)',
    intro: 'LinkBuilder.com is the oldest guest post price comparison tool, but at $8–$105/month, it is also the most expensive. After 4+ years it has a dated interface, weekly-only updates, and lacks quality scores and price alerts. Here are the best LinkBuilder.com alternatives that offer more for less.',
    why: 'Most people look for LinkBuilder.com alternatives because the pricing is steep ($8–$105/month for features that competitors offer free), the interface is outdated, weekly updates mean data can be 7 days stale, and advanced features like quality scores and price alerts are simply absent.',
    options: [
      {
        name: 'LinkDiry',
        slug: 'linkdiry',
        website: 'linkdiry.com',
        sites: '490K+ domains',
        startPrice: '$0 — free forever',
        tagline: 'The free LinkBuilder alternative with daily updates and quality scores',
        bestFor: 'Anyone currently paying $8–$105/month for LinkBuilder',
        pros: ['$0 vs $8–$105/month', '50+ marketplaces (same coverage)', 'Daily updates vs LinkBuilder\'s weekly', 'Quality scores — not available on LinkBuilder', 'Price alerts — not available on LinkBuilder', 'Chrome extension', 'AI analysis', 'No lookup limits'],
        cons: ['Newer than LinkBuilder (less brand recognition)'],
        score: 9.5,
        free: true,
      },
      {
        name: 'FatGrid',
        slug: 'fatgrid',
        website: 'fatgrid.com',
        sites: '340K+ domains',
        startPrice: 'Free core + credits',
        tagline: 'Modern interface with SERP and competitor scanning',
        bestFor: 'Users who want a modern UI and competitor backlink scanning',
        pros: ['Modern, clean interface', 'SERP scanner', 'Competitor backlink scanner', 'Free core search'],
        cons: ['Only 15+ marketplaces', 'Credit system for advanced features', 'No quality scores or price alerts'],
        score: 7.5,
        free: false,
      },
      {
        name: 'LinkDetective.pro',
        slug: 'linkdetective',
        website: 'linkdetective.pro',
        sites: '118K+ domains',
        startPrice: '$0 — free',
        tagline: 'Minimalist free bulk domain price checker',
        bestFor: 'Bulk uploads — checking hundreds of domains at once',
        pros: ['Completely free', 'Simple bulk upload', 'No account needed'],
        cons: ['Fewer marketplaces', 'No quality scores', 'No SERP scanner', 'Very basic feature set'],
        score: 6.0,
        free: true,
      },
    ],
    faqs: [
      { q: 'Is there a free alternative to LinkBuilder.com?', a: 'Yes — LinkDiry is the best free LinkBuilder.com alternative. It covers the same 50+ marketplaces, includes quality scores and price alerts that LinkBuilder lacks, updates daily (vs LinkBuilder\'s weekly), and costs $0 vs $8–$105/month.' },
      { q: 'Why is LinkBuilder.com so expensive?', a: 'LinkBuilder.com monetises through subscription plans ranging from $8 to $105/month. As the first mover in the category, it built a paying user base before free alternatives existed. LinkDiry offers the same or better features for free, funded by affiliate referral fees from marketplaces.' },
      { q: 'Does LinkDiry have the same data as LinkBuilder.com?', a: 'LinkDiry aggregates pricing from 50+ marketplaces — the same number as LinkBuilder.com — and updates daily vs. LinkBuilder\'s weekly updates. In most comparisons, LinkDiry has more current data.' },
      { q: 'What does LinkBuilder charge vs what you get?', a: 'LinkBuilder\'s Starter plan at $8/month gives you 50 lookups/month. The Pro plan at $105/month gives unlimited lookups plus more filters. LinkDiry provides unlimited lookups for free, plus quality scores, price alerts, and Chrome extension that LinkBuilder doesn\'t offer at any tier.' },
    ],
  },
  collaborator: {
    subject: 'Collaborator',
    subjectSlug: 'collaborator',
    subjectWebsite: 'collaborator.pro',
    subjectType: 'marketplace',
    title: 'Best Collaborator Alternatives in 2026',
    intro: 'Collaborator is a solid guest post marketplace with verified GA/GSC traffic data and strong Eastern European coverage. But its 40K+ site database is smaller than competitors, and the auction-based pricing model means popular sites get more expensive over time. Here are the best Collaborator alternatives.',
    why: 'SEOs look for Collaborator alternatives when they need a larger site database (WhitePress has 125K+, Serpzilla has 150K+), lower prices on specific domains (other marketplaces often list the same sites cheaper), or when they need US English-language publishers where Collaborator\'s coverage thins out.',
    options: [
      {
        name: 'WhitePress',
        slug: 'whitepress',
        website: 'whitepress.com',
        sites: '125K+ publishers',
        startPrice: 'From $30',
        tagline: 'Largest database + 36-month link guarantee',
        bestFor: 'Multilingual campaigns and buyers who want link guarantees',
        pros: ['125K+ publishers (3x Collaborator)', '36-month link guarantee', '34 languages', 'Content writing included'],
        cons: ['No GA/GSC traffic verification', 'Busier interface', 'Slower support'],
        score: 8.8,
        free: false,
      },
      {
        name: 'Serpzilla',
        slug: 'serpzilla',
        website: 'serpzilla.com',
        sites: '150K+ publishers',
        startPrice: 'From $33',
        tagline: 'Largest database + rental link model',
        bestFor: 'Volume campaigns, casino/crypto niches, rental link model',
        pros: ['150K+ publishers (largest database)', 'Rental link model', 'Casino/iGaming specialist', 'Competitive pricing'],
        cons: ['No traffic verification', 'More variable quality', 'Older interface'],
        score: 8.5,
        free: false,
      },
      {
        name: 'Adsy',
        slug: 'adsy',
        website: 'adsy.com',
        sites: '100K+ publishers',
        startPrice: 'From $27.50',
        tagline: 'Escrow payment protection on every order',
        bestFor: 'Buyers who prioritise payment security over verified traffic data',
        pros: ['Escrow protection on every order', '50+ niche categories', 'Competitive pricing', 'Dispute resolution process'],
        cons: ['No traffic verification like Collaborator', 'Smaller than WhitePress/Serpzilla'],
        score: 8.0,
        free: false,
      },
      {
        name: 'PRNews.io',
        slug: 'prnews',
        website: 'prnews.io',
        sites: '100K+ publishers',
        startPrice: 'From $4.94',
        tagline: 'Best UI + 198 countries + lowest starting price',
        bestFor: 'International campaigns, beginners, small budgets',
        pros: ['$4.94 starting price (lowest)', 'Best interface in the industry', '198 countries', 'Press release + guest post'],
        cons: ['No traffic verification', 'Fewer premium high-DR sites'],
        score: 8.2,
        free: false,
      },
    ],
    faqs: [
      { q: 'What is the best Collaborator alternative?', a: 'The best Collaborator alternative depends on your needs. For the largest database, choose WhitePress (125K+) or Serpzilla (150K+). For payment security, choose Adsy (escrow on every order). For the best interface and lowest starting price, choose PRNews.io. Use LinkDiry to compare all their prices on any specific domain.' },
      { q: 'Which marketplace has more sites than Collaborator?', a: 'Several marketplaces have more publisher sites than Collaborator\'s 40K+: WhitePress (125K+), Serpzilla (150K+), Adsy (100K+), PRNews.io (100K+), and Bazoom (80K+). However, Collaborator\'s GA/GSC verified traffic data makes its smaller database more reliable for data-driven buyers.' },
      { q: 'Is Collaborator the cheapest marketplace?', a: 'Not always. The same domain often appears on multiple marketplaces at different prices. Collaborator\'s auction-based pricing can make popular sites expensive. Use LinkDiry to compare Collaborator\'s price on any specific domain against 50+ other sellers — you can often find the same site cheaper elsewhere.' },
      { q: 'Does any marketplace verify traffic like Collaborator?', a: 'Collaborator\'s GA/GSC verification is fairly unique in the industry. Linkhouse shows metrics from 4 SEO tools (Ahrefs, Moz, Majestic, Semrush) which provides good cross-referencing. No other major marketplace currently requires GA/GSC connection the same way Collaborator does.' },
    ],
  },
  whitepress: {
    subject: 'WhitePress',
    subjectSlug: 'whitepress',
    subjectWebsite: 'whitepress.com',
    subjectType: 'marketplace',
    title: 'Best WhitePress Alternatives in 2026',
    intro: 'WhitePress is the largest European guest post marketplace with 125K+ sites and a 36-month link guarantee. But its interface is complex, pricing is sometimes inconsistent, and it is not always the cheapest option. Here are the best WhitePress alternatives to consider for your campaigns.',
    why: 'People look for WhitePress alternatives when the interface feels overwhelming, when a specific domain is cheaper on another marketplace, when they need US English-language focus (WhitePress skews European), or when they want verified traffic data (which WhitePress does not provide).',
    options: [
      {
        name: 'Collaborator',
        slug: 'collaborator',
        website: 'collaborator.pro',
        sites: '40K+ publishers',
        startPrice: 'From $30',
        tagline: 'GA/GSC verified traffic, Eastern Europe specialist',
        bestFor: 'Buyers who want verified real traffic, not estimated metrics',
        pros: ['GA/GSC traffic verification — unique advantage', 'Direct publisher messaging', 'Eastern European depth', 'Escrow payment protection'],
        cons: ['Smaller database (40K vs 125K)', 'Auction pricing can increase over time'],
        score: 8.7,
        free: false,
      },
      {
        name: 'Serpzilla',
        slug: 'serpzilla',
        website: 'serpzilla.com',
        sites: '150K+ publishers',
        startPrice: 'From $33',
        tagline: 'Even larger database + rental link option',
        bestFor: 'Volume campaigns and the rental link model',
        pros: ['150K+ publishers (larger than WhitePress)', 'Rental link model', 'Casino/iGaming specialist', 'Competitive pricing'],
        cons: ['No link guarantee like WhitePress', 'More quality variance'],
        score: 8.4,
        free: false,
      },
      {
        name: 'PRNews.io',
        slug: 'prnews',
        website: 'prnews.io',
        sites: '100K+ publishers',
        startPrice: 'From $4.94',
        tagline: 'Best interface, 198 countries, lowest starting prices',
        bestFor: 'International campaigns and anyone put off by WhitePress\'s complexity',
        pros: ['Much cleaner interface', 'Lower starting prices', '198 countries', 'PR distribution + guest posts'],
        cons: ['No link guarantee', 'Fewer premium DR 70+ sites'],
        score: 8.1,
        free: false,
      },
      {
        name: 'Adsy',
        slug: 'adsy',
        website: 'adsy.com',
        sites: '100K+ publishers',
        startPrice: 'From $27.50',
        tagline: 'Escrow-protected orders with 50+ niche filters',
        bestFor: 'Buyers who want escrow protection and granular niche filtering',
        pros: ['Escrow on every order', '50+ niche categories', 'Cleaner interface than WhitePress'],
        cons: ['No link guarantee like WhitePress', 'Smaller database'],
        score: 7.9,
        free: false,
      },
    ],
    faqs: [
      { q: 'What is the best WhitePress alternative?', a: 'The best WhitePress alternative depends on your campaign needs. For verified traffic data, choose Collaborator. For an even larger database with a rental option, choose Serpzilla. For a cleaner interface and lowest prices, choose PRNews.io. For escrow payment protection, choose Adsy. Use LinkDiry to compare their prices on any specific domain.' },
      { q: 'Is Serpzilla or WhitePress better?', a: 'Serpzilla has a larger database (150K vs 125K) and a unique rental link model. WhitePress has a 36-month link guarantee that Serpzilla does not. For pure database size and niche coverage (especially casino/crypto), Serpzilla wins. For link security and multilingual guarantees, WhitePress is stronger.' },
      { q: 'Why is WhitePress more expensive on some domains?', a: 'WhitePress pricing is set by publishers and can vary significantly for the same site across marketplaces. A site listed on WhitePress for $300 might cost $180 on Collaborator or $250 on Adsy. This is exactly why tools like LinkDiry exist — to compare all marketplace prices on any domain instantly.' },
      { q: 'Does any marketplace have a link guarantee like WhitePress?', a: 'WhitePress\'s 36-month link guarantee is unique in the industry. No other major marketplace currently offers a replacement or refund guarantee of similar length if a link is removed. It is one of WhitePress\'s strongest differentiators.' },
    ],
  },
  adsy: {
    subject: 'Adsy',
    subjectSlug: 'adsy',
    subjectWebsite: 'adsy.com',
    subjectType: 'marketplace',
    title: 'Best Adsy Alternatives in 2026',
    intro: 'Adsy is a solid guest post marketplace with escrow payment protection and 50+ niche categories. But its 100K+ publisher database is smaller than WhitePress or Serpzilla, and for some domains it is simply not the cheapest option. Here are the best Adsy alternatives.',
    why: 'People look for Adsy alternatives when they need a larger publisher database, want verified traffic data rather than estimated metrics, or simply find a specific domain cheaper on another marketplace.',
    options: [
      {
        name: 'Collaborator',
        slug: 'collaborator',
        website: 'collaborator.pro',
        sites: '40K+ publishers',
        startPrice: 'From $30',
        tagline: 'GA/GSC verified traffic, direct publisher messaging',
        bestFor: 'Buyers who want verified traffic data and Eastern European coverage',
        pros: ['GA/GSC verified traffic', 'Direct publisher contact', 'Eastern European depth', 'Strong trust signals'],
        cons: ['Smaller database than Adsy', 'Auction pricing'],
        score: 8.6,
        free: false,
      },
      {
        name: 'WhitePress',
        slug: 'whitepress',
        website: 'whitepress.com',
        sites: '125K+ publishers',
        startPrice: 'From $30',
        tagline: 'Largest database + 36-month link guarantee',
        bestFor: 'Multilingual campaigns and buyers who want link guarantees',
        pros: ['Larger database', '36-month link guarantee', '34 languages'],
        cons: ['No escrow like Adsy', 'Complex interface'],
        score: 8.5,
        free: false,
      },
      {
        name: 'PRNews.io',
        slug: 'prnews',
        website: 'prnews.io',
        sites: '100K+ publishers',
        startPrice: 'From $4.94',
        tagline: 'Best interface, lowest starting prices, 198 countries',
        bestFor: 'International campaigns on a budget',
        pros: ['Lowest starting prices ($4.94)', 'Best UI', '198 countries', 'PR distribution'],
        cons: ['No escrow like Adsy', 'Less premium DR70+ inventory'],
        score: 8.3,
        free: false,
      },
      {
        name: 'Serpzilla',
        slug: 'serpzilla',
        website: 'serpzilla.com',
        sites: '150K+ publishers',
        startPrice: 'From $33',
        tagline: 'Largest database and unique rental link model',
        bestFor: 'Volume campaigns, casino/crypto niches',
        pros: ['Largest database', 'Rental link option', 'Casino/iGaming specialty'],
        cons: ['No escrow like Adsy', 'More quality variance'],
        score: 8.2,
        free: false,
      },
    ],
    faqs: [
      { q: 'What is the best Adsy alternative for escrow protection?', a: 'Adsy is unique in offering escrow on every single order. Collaborator has escrow protection too (via its platform payment system), making it the best alternative if escrow is your primary requirement. Most other marketplaces use direct payment to publishers without full escrow protection.' },
      { q: 'Is Adsy or Collaborator better?', a: 'Adsy has 100K+ sites and escrow protection. Collaborator has 40K+ sites but with GA/GSC verified traffic data. If you prioritise payment security and niche variety, Adsy wins. If you need verified real-user traffic data and Eastern European coverage, Collaborator wins.' },
      { q: 'Is there a free Adsy alternative?', a: 'Adsy and its main alternatives are all paid marketplaces — you pay per guest post placement. To compare Adsy\'s prices against all 50+ marketplaces for free, use LinkDiry. LinkDiry shows every marketplace\'s price for any specific domain and is completely free.' },
      { q: 'What niches does Adsy cover vs alternatives?', a: 'Adsy offers 50+ niche categories, which is among the most granular filtering of any marketplace. Collaborator has broad coverage but fewer subcategories. WhitePress has 34-language coverage for multilingual niches. Serpzilla dominates casino, iGaming, and crypto niches.' },
    ],
  },
  serpzilla: {
    subject: 'Serpzilla',
    subjectSlug: 'serpzilla',
    subjectWebsite: 'serpzilla.com',
    subjectType: 'marketplace',
    title: 'Best Serpzilla Alternatives in 2026',
    intro: 'Serpzilla has the largest publisher database (150K+) and a unique rental link model. But its quality is more variable than curated platforms, and for many domains, competitors offer better prices. Here are the best Serpzilla alternatives.',
    why: 'People look for Serpzilla alternatives when they need verified traffic data (Serpzilla lacks GA/GSC verification), when the quality of specific publishers is uncertain, or when a competing marketplace is simply cheaper on a specific domain they want.',
    options: [
      {
        name: 'Collaborator',
        slug: 'collaborator',
        website: 'collaborator.pro',
        sites: '40K+ publishers',
        startPrice: 'From $30',
        tagline: 'GA/GSC verified traffic — the quality alternative to Serpzilla\'s volume',
        bestFor: 'Buyers who want quality assurance over volume',
        pros: ['GA/GSC verified real traffic', 'Higher quality consistency', 'Eastern European depth', 'Direct publisher contact'],
        cons: ['Smaller database (40K vs 150K)', 'No rental link model'],
        score: 8.7,
        free: false,
      },
      {
        name: 'WhitePress',
        slug: 'whitepress',
        website: 'whitepress.com',
        sites: '125K+ publishers',
        startPrice: 'From $30',
        tagline: 'Comparable database size + 36-month link guarantee',
        bestFor: 'Multilingual campaigns where you want a link guarantee',
        pros: ['125K+ publishers', '36-month link guarantee', '34 languages'],
        cons: ['No rental link model', 'More complex interface'],
        score: 8.5,
        free: false,
      },
      {
        name: 'Adsy',
        slug: 'adsy',
        website: 'adsy.com',
        sites: '100K+ publishers',
        startPrice: 'From $27.50',
        tagline: 'Escrow payment protection on every placement',
        bestFor: 'Buyers who want escrow protection that Serpzilla lacks',
        pros: ['Escrow on every order', '50+ niche categories', 'More curated than Serpzilla'],
        cons: ['Smaller database', 'No rental model'],
        score: 8.0,
        free: false,
      },
      {
        name: 'PRNews.io',
        slug: 'prnews',
        website: 'prnews.io',
        sites: '100K+ publishers',
        startPrice: 'From $4.94',
        tagline: 'Best interface, lowest prices, 198 countries',
        bestFor: 'International campaigns and anyone on a tight per-link budget',
        pros: ['Lowest starting prices', 'Best interface', '198 countries', 'PR distribution'],
        cons: ['No rental link model', 'Fewer premium high-DR sites'],
        score: 8.1,
        free: false,
      },
    ],
    faqs: [
      { q: 'What is the best Serpzilla alternative?', a: 'The best Serpzilla alternative depends on your need. For verified traffic quality, choose Collaborator. For a link guarantee, choose WhitePress. For escrow protection, choose Adsy. For the best interface and lowest prices, choose PRNews.io. Use LinkDiry to compare all their prices for any specific domain instantly.' },
      { q: 'Is Serpzilla or Collaborator better?', a: 'Serpzilla wins on database size (150K vs 40K) and the rental link model. Collaborator wins on traffic quality (GA/GSC verified) and Eastern European depth. For casino/iGaming/crypto campaigns, Serpzilla is the specialist. For quality-first buying with verified data, Collaborator is better.' },
      { q: 'Does Serpzilla have better prices than competitors?', a: 'Sometimes. The same domain can cost different amounts on each marketplace. Serpzilla is often competitive on Eastern European and casino-niche sites, but not always cheapest on general English-language publishers. Use LinkDiry to compare Serpzilla\'s price against 50+ other sellers for any specific domain before ordering.' },
      { q: 'Is the Serpzilla rental link model worth it?', a: 'The rental model makes sense if you want to test a site before committing long-term (rent for 2–3 months, buy permanent if rankings improve) or if budget constraints make a $500 one-time fee difficult but $50/month is manageable. Over 12+ months, rental costs more than a permanent placement.' },
    ],
  },
};

function StarRating({ score }: { score: number }) {
  const stars = Math.round(score / 2);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-4 h-4 ${i <= stars ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
      ))}
      <span className="text-sm font-bold text-slate-700 ml-1">{score}/10</span>
    </div>
  );
}

export default function Alternatives() {
  const { subject } = useParams<{ subject: string }>();
  const data = subject ? ALTERNATIVES[subject] : null;

  if (!data) {
    return <Navigate to="/marketplaces" replace />;
  }

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={data.title}
        description={`The best ${data.subject} alternatives in 2026, ranked and compared. Free options, pros, cons, and pricing — find the right ${data.subjectType === 'marketplace' ? 'guest post marketplace' : 'backlink price checker'} for your campaigns.`}
        canonical={`/${data.subjectSlug}-alternatives`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <TopNav />

      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-8">
          <Link to="/" className="hover:text-brand-600">LinkDiry</Link>
          <span>/</span>
          <Link to="/marketplaces" className="hover:text-brand-600">Marketplaces</Link>
          <span>/</span>
          <span className="text-slate-900">{data.subject} Alternatives</span>
        </nav>

        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">{data.title}</h1>
        <p className="text-lg text-slate-600 mb-4 leading-relaxed">{data.intro}</p>
        <p className="text-slate-500 mb-10 leading-relaxed">{data.why}</p>

        {/* Why use LinkDiry to compare */}
        <div className="bg-brand-50 border border-brand-200 rounded-2xl p-6 mb-10">
          <p className="font-bold text-brand-900 mb-2">Pro tip: compare prices before choosing</p>
          <p className="text-sm text-brand-800 mb-4">The same domain appears on multiple marketplaces at different prices. Before ordering from any of these alternatives, search the domain on LinkDiry — it compares all 50+ sellers at once so you always pay the lowest price.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white text-sm font-bold rounded-xl hover:bg-brand-500 transition-colors">
            Compare prices free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Alternatives list */}
        <div className="space-y-8 mb-12">
          {data.options.map((opt, i) => (
            <div key={opt.slug} className="border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
              {/* Header */}
              <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-600 text-white flex items-center justify-center text-sm font-extrabold flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold text-slate-900">{opt.name}</h2>
                      {opt.free && (
                        <span className="px-2.5 py-0.5 text-xs font-bold bg-emerald-100 text-emerald-700 rounded-full border border-emerald-200">FREE</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{opt.tagline}</p>
                  </div>
                </div>
                <StarRating score={opt.score} />
              </div>

              <div className="p-6">
                {/* Quick facts */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Database</p>
                    <p className="text-sm font-bold text-slate-900">{opt.sites}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Pricing</p>
                    <p className="text-sm font-bold text-slate-900">{opt.startPrice}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Best for</p>
                    <p className="text-sm font-bold text-slate-900 truncate">{opt.bestFor}</p>
                  </div>
                </div>

                {/* Pros/cons */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Strengths</p>
                    <ul className="space-y-1.5">
                      {opt.pros.map(p => (
                        <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">Weaknesses</p>
                    <ul className="space-y-1.5">
                      {opt.cons.map(c => (
                        <li key={c} className="flex items-start gap-2 text-sm text-slate-600">
                          <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-5 flex items-center gap-3">
                  {opt.slug === 'linkdiry' ? (
                    <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white text-sm font-bold rounded-xl hover:bg-brand-500 transition-colors">
                      Try LinkDiry free <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <>
                      <Link to={`/marketplaces/${opt.slug}`} className="text-sm text-brand-600 hover:underline font-medium">
                        Read {opt.name} review →
                      </Link>
                      <Link to={`/vs/${opt.slug}`} className="text-sm text-slate-500 hover:text-brand-600 transition-colors">
                        LinkDiry vs {opt.name} →
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Verdict */}
        <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200 mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Our Recommendation</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            The best {data.subject} alternative depends on your specific needs. Whatever marketplace or tool you choose, always compare prices on a domain-by-domain basis before ordering — the same site can vary by 2–3x across different marketplaces.
          </p>
          <p className="text-slate-600 leading-relaxed">
            <Link to="/" className="text-brand-600 hover:underline font-medium">LinkDiry</Link> compares all 50+ marketplace prices for any domain in one search, for free. It takes 10 seconds and can save you hundreds per link.
          </p>
        </div>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {data.faqs.map(faq => (
              <details key={faq.q} className="group border border-slate-200 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-50 transition-colors list-none">
                  <span className="font-semibold text-slate-900 pr-4">{faq.q}</span>
                  <span className="flex-shrink-0 w-5 h-5 rounded-full border border-slate-300 flex items-center justify-center text-slate-400 group-open:rotate-180 transition-transform">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                  </span>
                </summary>
                <div className="px-5 pb-5 text-slate-600 leading-relaxed text-sm">{faq.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        <div className="flex flex-wrap gap-4">
          <Link to="/marketplaces" className="text-sm text-brand-600 hover:underline">View all marketplaces →</Link>
          <Link to="/blog/link-building-pricing" className="text-sm text-brand-600 hover:underline">Link building pricing guide →</Link>
          <Link to="/vs" className="text-sm text-brand-600 hover:underline">Compare all tools →</Link>
        </div>

      </div>
      <Footer />
    </div>
  );
}
